/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import {
  Form,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  message,
  Select,
  Statistic,
  Space,
  Modal,
  Popconfirm,
  Checkbox,
  Alert,
} from "antd";

import dayjs from "dayjs";

import "dayjs/locale/es";

import { ChevronLeft, DollarSign, Building2 } from "lucide-react";

import FetchSelect from "../ui-components/FetchSelect";
import useGroups from "../hooks/useGroups";
import useHotels from "../hooks/useHotels";
import useUsers from "../hooks/useUsers";
import useExcursions from "../hooks/useExcursions";
import CountUp from "react-countup";
import { calculatePassengerTotal } from "../utils/passengerCalculations";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import usePassengers from "../hooks/usePassengers";
import useCompanies from "../hooks/useCompanies";
import useAuth from "../hooks/useAuth";
import { useNavigate } from "react-router";
import { useParams } from "react-router";

dayjs.locale("es");

const { Option } = Select;

const roleFormat = {
  SELLER: "Vendedor",
  GUIDE: "Guía",
  COORDINATOR: "Coordinador",
  OFFICE_WORKER: "Oficina",
};

function PassengerForm({ onModeSet, passengerId: passengerIdProp, onSuccess, isModal }) {
  const { passengerId: passengerIdParam } = useParams();
  const passengerId = passengerIdProp || passengerIdParam;
  const navigate = useNavigate();

  const { getGroups, groups } = useGroups();
  const { getHotels } = useHotels();
  const { getUsers, getUserDetail, users } = useUsers();
  const { getExcursions, excursions } = useExcursions();
  const {
    getPaymentMethods,
    paymentMethods,
    createPassenger,
    updatePassenger,
    loading,
    getPassengerDetail,
    passenger,
    deletePassenger,
    setPassenger,
  } = usePassengers();
  const { getCompanyDetail } = useCompanies();
  const { getUser } = useAuth();

  const [form] = Form.useForm();

  const [amountPayed, setAmountPayed] = React.useState(0);
  const [debt, setDebt] = React.useState(0);
  const [userTypes, setUserTypes] = React.useState({});

  const [isSelectedExcursionFinished, setIsSelectedExcursionFinished] = React.useState(false);
  const [isSelectedGroupMissing, setIsSelectedGroupMissing] = React.useState(false);

  const [totalToPay, setTotalToPay] = React.useState(0);
  const [automaticTotalToPay, setAutomaticTotalToPay] = React.useState(0);
  const [totalWithoutDiscount, setTotalWithoutDiscount] = React.useState(0);
  const [currentGroupCommission, setCurrentGroupCommission] = React.useState(null);

  const selectedGroup = Form.useWatch("group", form);
  const selectedExcursion = Form.useWatch("scheduledExcursion", form);
  const adults = Form.useWatch("adultsCount", form);
  const minors = Form.useWatch("minorsCount", form);
  const adultsAbsence = Form.useWatch("adultsAbsentCount", form);
  const minorsAbsence = Form.useWatch("minorsAbsentCount", form);
  const payments = Form.useWatch("payments", form);

  // Custom pricing fields
  const discountType = Form.useWatch("discountType", form);
  const discountPercentage = Form.useWatch("discountPercentage", form);
  const customPriceAdult = Form.useWatch("customPriceAdult", form);
  const customPriceMinor = Form.useWatch("customPriceMinor", form);
  const discountAffectsCommission = Form.useWatch("discountAffectsCommission", form);

  useEffect(() => {
    getExcursions({ status: "ACTIVE" });
    getPaymentMethods();
    getGroups({
      check_in_date_range_before: dayjs().format("YYYY-MM-DD"),
      check_out_date_range_after: dayjs().format("YYYY-MM-DD"),
    });

    if (passengerId) {
      getPassengerDetail(passengerId);
    }
  }, []);

  useEffect(() => {
    if (!passenger) return;

    getGroups({
      check_in_date_before: dayjs(passenger.createdAt).format("YYYY-MM-DD"),
      check_out_date_after: dayjs(passenger.createdAt).format("YYYY-MM-DD"),
    });

    setTotalToPay(passenger.totalAmount);

    // Determine discount type from existing data
    let editDiscountType = "none";
    if (passenger.customPriceAdult !== null || passenger.customPriceMinor !== null) {
      editDiscountType = "fixed";
    } else if (passenger.discountPercentage !== null) {
      editDiscountType = "percentage";
    }

    form.setFieldsValue({
      group: { value: passenger.group.id },
      hotel: { value: passenger.hotel.id },
      seller: { value: passenger.seller.id },
      scheduledExcursion: { value: passenger.scheduledExcursion.id },
      name: passenger.name,
      adultsCount: passenger.adultsCount,
      minorsCount: passenger.minorsCount,
      babyCount: passenger.babyCount,
      freeCount: passenger.freeCount,
      adultsAbsentCount: passenger.adultsAbsentCount,
      minorsAbsentCount: passenger.minorsAbsentCount,
      babyAbsentCount: passenger.babyAbsentCount,
      freeAbsentCount: passenger.freeAbsentCount,
      observation: passenger.observation,
      guideObservation: passenger.guideObservation,
      // Custom pricing fields
      discountType: editDiscountType,
      discountPercentage: passenger.discountPercentage,
      customPriceAdult: passenger.customPriceAdult,
      customPriceMinor: passenger.customPriceMinor,
      discountAffectsCommission: passenger.discountAffectsCommission ?? true,
      payments: passenger.payments.map((payment) => ({
        user: { value: payment.user },
        paymentMethod: payment.paymentMethod,
        amount: payment.amount,
      })),
    });

    setUserTypesInEdit(passenger);
  }, [passenger]);

  // Detect if the passenger's group is missing from the fetched groups (e.g., group already left the city)
  useEffect(() => {
    if (!passenger) {
      setIsSelectedGroupMissing(false);
      return;
    }

    const groupId = passenger?.group?.id;
    if (!groupId) {
      setIsSelectedGroupMissing(false);
      return;
    }

    const found = groups.results?.some((g) => g.id === groupId);
    setIsSelectedGroupMissing(!found);
  }, [passenger, groups]);

  useEffect(() => {
    if (!selectedGroup || !selectedGroup.value) return;

    getUsers({ available_to_sell_group_id: selectedGroup.value });
  }, [selectedGroup]);

  useEffect(() => {
    if (!selectedGroup || !selectedGroup.value) {
      setCurrentGroupCommission(null);
      return;
    }

    // Buscar el grupo seleccionado en la lista de grupos
    const group = groups.results?.find(g => g.id === selectedGroup.value);
    if (group?.company?.id) {
      // Obtener detalles de la empresa para conocer el porcentaje de comisión
      getCompanyDetail(group.company.id).then(companyDetail => {
        setCurrentGroupCommission({
          companyId: group.company.id,
          companyName: group.company.name,
          coordinatorCommission: companyDetail.coordinatorCommission,
          companyCommission: companyDetail.companyCommission
        });
      }).catch(() => {
        setCurrentGroupCommission(null);
      });
    }
  }, [selectedGroup, groups]);

  const setUserTypesInEdit = (passenger) => {
    const userTypes = {};

    passenger.payments.forEach(async (payment, index) => {
      const user = await getUserDetail(payment.user);

      user && (userTypes[index] = user.userType);
    });

    setUserTypes(userTypes);
  };

  useEffect(() => {
    if (selectedExcursion && excursions.results) {
      const excursionObject = excursions.results.find(
        (excursion) => excursion.id === selectedExcursion.value
      );

      // If excursion not found or missing excursionType, mark as finished/unavailable
      if (!excursionObject || !excursionObject.excursionType) {
        console.warn("Excursión no encontrada o finalizada:", selectedExcursion.value);
        setIsSelectedExcursionFinished(true);
        return;
      }

      setIsSelectedExcursionFinished(false);

      // Build custom pricing params based on discount type
      const customPricingParams = {};
      if (discountType === "percentage" && discountPercentage) {
        customPricingParams.discountPercentage = discountPercentage;
      } else if (discountType === "fixed") {
        if (customPriceAdult !== null && customPriceAdult !== undefined) {
          customPricingParams.customPriceAdult = customPriceAdult;
        }
        if (customPriceMinor !== null && customPriceMinor !== undefined) {
          customPricingParams.customPriceMinor = customPriceMinor;
        }
      }

      const totalSum = calculatePassengerTotal({
        adultsCount: adults || 0,
        minorsCount: minors || 0,
        adultsAbsentCount: adultsAbsence || 0,
        minorsAbsentCount: minorsAbsence || 0,
        priceAdult: excursionObject.excursionType.priceAdult,
        priceMinor: excursionObject.excursionType.priceMinor,
        ...customPricingParams,
      });

      // Calcular total SIN descuento (precio base)
      const totalWithoutDiscountCalc = calculatePassengerTotal({
        adultsCount: adults || 0,
        minorsCount: minors || 0,
        adultsAbsentCount: adultsAbsence || 0,
        minorsAbsentCount: minorsAbsence || 0,
        priceAdult: excursionObject.excursionType.priceAdult,
        priceMinor: excursionObject.excursionType.priceMinor,
        // Sin pasar customPriceAdult, customPriceMinor, ni discountPercentage
      });
      setTotalWithoutDiscount(totalWithoutDiscountCalc);

      // Siempre actualizar el total cuando cambian las cantidades o bajas
      // Asegurar que nunca sea menor a 0
      setTotalToPay(Math.max(0, totalSum));

      setAutomaticTotalToPay(totalSum);

      const debtValue = totalSum - amountPayed > 0 ? totalSum - amountPayed : 0;

      setDebt(debtValue);
    }
  }, [
    selectedExcursion,
    excursions,
    adults,
    minors,
    adultsAbsence,
    minorsAbsence,
    discountType,
    discountPercentage,
    customPriceAdult,
    customPriceMinor,
  ]);

  useEffect(() => {
    if (payments) {
      const totalPaid = payments.reduce(
        (sum, payment) => sum + (payment?.amount || 0),
        0
      );

      setAmountPayed(totalPaid);

      const debtValue = totalToPay - totalPaid > 0 ? totalToPay - totalPaid : 0;
      setDebt(debtValue);
    }
  }, [payments, totalToPay]);

  const onFinish = async (values) => {
    if (!formValidations()) {
      return;
    }

    if (!values.payments || values.payments.length === 0) {
      Modal.confirm({
        title: "¿Estás seguro que quieres crear un pasajero sin ningún pago?",
        onOk: () => handleFormSubmit(values),
      });
      return;
    }

    if (amountPayed > totalToPay) {
      Modal.confirm({
        title:
          "Hay más dinero pagado que el monto total, ¿Seguro/a que esto es correcto?",
        onOk: () => handleFormSubmit(values),
      });
      return;
    }

    handleFormSubmit(values);
  };

  const handleFormSubmit = async (values) => {
    // Prepare custom pricing fields based on discount type
    let customPricingFields = {
      customPriceAdult: null,
      customPriceMinor: null,
      discountPercentage: null,
      discountAffectsCommission: values.discountAffectsCommission ?? true,
    };

    if (values.discountType === "percentage") {
      customPricingFields.discountPercentage = values.discountPercentage || null;
    } else if (values.discountType === "fixed") {
      customPricingFields.customPriceAdult = values.customPriceAdult ?? null;
      customPricingFields.customPriceMinor = values.customPriceMinor ?? null;
    }

    const formattedValues = {
      ...values,
      group: selectedGroup.value,
      scheduledExcursion: selectedExcursion.value,
      hotel: values.hotel.value,
      seller: values.seller.value,
      totalAmount: totalToPay,
      ...customPricingFields,
      payments: values.payments?.map((payment) => ({
        amount: payment.amount,
        user: payment.user.value,
        paymentMethod: payment.paymentMethod ? payment.paymentMethod : null,
      })),
    };

    // Remove the discountType field as it's only used in the frontend
    delete formattedValues.discountType;

    passengerId
      ? await manageUpdate({ ...formattedValues, id: passengerId })
      : await manageCreate(formattedValues);
  };

  const manageUpdate = async (data) => {
    await updatePassenger(data);
    form.resetFields();

    if (onSuccess) {
      onSuccess();
    } else {
      navigate("/pasajeros");
    }
  };

  const manageCreate = async (data) => {
    await createPassenger(data);

    console.log(data);

    // Verificar si hay pagos antes de acceder al primer pago
    const hasPayments = data.payments && data.payments.length > 0;
    if (hasPayments) {
      console.log(data.payments[0]);
    }

    form.setFieldsValue({
      name: "",
      adultsCount: 0,
      minorsCount: 0,
      babyCount: 0,
      freeCount: 0,
      adultsAbsentCount: 0,
      minorsAbsentCount: 0,
      babyAbsentCount: 0,
      freeAbsentCount: 0,
      observation: "",
      guideObservation: "",
      // Reset custom pricing fields
      discountType: "none",
      discountPercentage: null,
      customPriceAdult: null,
      customPriceMinor: null,
      discountAffectsCommission: true,
      payments: hasPayments ? [
        {
          user: { value: data.payments[0].user },
          paymentMethod: data.payments[0].paymentMethod,
          amount: 0,
        },
      ] : [],
    });
  };

  const onFinishFailed = (errorInfo) => {
    message.error("Error al enviar el formulario");
  };

  const formatter = (value) => (
    <CountUp duration={0.5} end={value} prefix="$" separator="," />
  );

  const formValidations = () => {
    const fieldsToValidate = [
      { element: "adultsCount", absence: "adultsAbsentCount", label: "Adulto" },
      { element: "minorsCount", absence: "minorsAbsentCount", label: "Menor" },
      { element: "babyCount", absence: "babyAbsentCount", label: "Bebé" },
      { element: "freeCount", absence: "freeAbsentCount", label: "Liberado" },
    ];

    for (const field of fieldsToValidate) {
      const elementValue = form.getFieldValue(field.element);
      const absenceValue = form.getFieldValue(field.absence);

      if (absenceValue > elementValue) {
        message.error(
          `El valor de ${field.label} debe ser mayor o igual que la ausencia de ${field.label}`
        );
        return false;
      }
    }

    // Validar payment_method para usuarios OFFICE_WORKER
    const payments = form.getFieldValue("payments") || [];
    for (let i = 0; i < payments.length; i++) {
      if (userTypes[i]?.includes("OFFICE") && !payments[i]?.paymentMethod) {
        message.error("Los pagos a trabajadores de oficina requieren método de pago");
        return false;
      }
    }

    return true;
  };

  const handleUserChange = async (userId, name) => {
    const user = await getUserDetail(userId);
    setUserTypes((prev) => ({
      ...prev,
      [name]: user ? user.userType : null,
    }));
  };

  const handleCoordinatorCommission = async () => {
    try {
      let companyInfo, passengerTotalAmount, groupCoordinator;

      if (passengerId && passenger) {
        // Modo edición - usar datos del pasajero existente
        if (!passenger?.group?.company?.id) {
          message.error("No se puede obtener la información de la empresa");
          return;
        }
        companyInfo = await getCompanyDetail(passenger.group.company.id);
        passengerTotalAmount = passenger.totalAmount;
        groupCoordinator = passenger.group.coordinator;
      } else {
        // Modo creación - usar información del grupo seleccionado y total calculado
        if (!selectedGroup || !selectedGroup.value) {
          message.error("Debe seleccionar un grupo válido primero");
          return;
        }

        // Buscar el grupo seleccionado en la lista de grupos
        const group = groups.results?.find(g => g.id === selectedGroup.value);
        if (!group?.company?.id) {
          message.error("El grupo seleccionado no tiene una empresa asociada");
          return;
        }

        companyInfo = await getCompanyDetail(group.company.id);
        passengerTotalAmount = totalToPay;
        groupCoordinator = group.coordinator;
      }
      
      if (!companyInfo.coordinatorCommission) {
        message.error(`La empresa "${companyInfo.name || 'seleccionada'}" no tiene configurado un porcentaje de comisión para coordinador`);
        return;
      }

      if (!groupCoordinator?.id) {
        message.error("El grupo no tiene un coordinador asignado");
        return;
      }

      if (!passengerTotalAmount || passengerTotalAmount <= 0) {
        message.error("El monto total del pasajero debe ser mayor a 0");
        return;
      }

      // Calcular el monto de comisión
      const commissionAmount = (passengerTotalAmount * companyInfo.coordinatorCommission) / 100;
      
      // Obtener los pagos actuales del formulario
      const currentPayments = form.getFieldValue('payments') || [];
      
      // Agregar el nuevo pago de comisión coordinador al array
      const newPayment = {
        user: { value: groupCoordinator.id },
        paymentMethod: null, // Pago al coordinador
        amount: commissionAmount,
        description: `Comisión coordinador (${companyInfo.coordinatorCommission}%)`
      };

      // Actualizar el formulario con el nuevo pago
      form.setFieldsValue({
        payments: [...currentPayments, newPayment]
      });
      
      message.success(`Comisión de coordinador agregada: $${commissionAmount.toLocaleString()} (se guardará al enviar el formulario)`);
    } catch (error) {
      console.error("Error al agregar comisión de coordinador:", error);
      message.error("Error al agregar la comisión de coordinador");
    }
  };

  const handleCompanyCommission = async () => {
    try {
      let companyInfo, passengerTotalAmount, groupCoordinator;

      if (passengerId && passenger) {
        // Modo edición - usar datos del pasajero existente
        if (!passenger?.group?.company?.id) {
          message.error("No se puede obtener la información de la empresa");
          return;
        }
        companyInfo = await getCompanyDetail(passenger.group.company.id);
        passengerTotalAmount = passenger.totalAmount;
        groupCoordinator = passenger.group.coordinator;
      } else {
        // Modo creación - usar información del grupo seleccionado y total calculado
        if (!selectedGroup || !selectedGroup.value) {
          message.error("Debe seleccionar un grupo válido primero");
          return;
        }

        // Buscar el grupo seleccionado en la lista de grupos
        const group = groups.results?.find(g => g.id === selectedGroup.value);
        if (!group?.company?.id) {
          message.error("El grupo seleccionado no tiene una empresa asociada");
          return;
        }

        companyInfo = await getCompanyDetail(group.company.id);
        passengerTotalAmount = totalToPay;
        groupCoordinator = group.coordinator;
      }
      
      if (!companyInfo.companyCommission) {
        message.error(`La empresa "${companyInfo.name || 'seleccionada'}" no tiene configurado un porcentaje de comisión para empresa`);
        return;
      }

      if (!groupCoordinator?.id) {
        message.error("El grupo no tiene un coordinador asignado");
        return;
      }

      if (!passengerTotalAmount || passengerTotalAmount <= 0) {
        message.error("El monto total del pasajero debe ser mayor a 0");
        return;
      }

      // Calcular el monto de comisión
      const commissionAmount = (passengerTotalAmount * companyInfo.companyCommission) / 100;
      
      // Obtener los pagos actuales del formulario
      const currentPayments = form.getFieldValue('payments') || [];
      
      // Agregar el nuevo pago de comisión empresa al array
      const newPayment = {
        user: { value: groupCoordinator.id },
        paymentMethod: paymentMethods.length > 0 ? paymentMethods[0].id : null, // Usar primer método de pago disponible
        amount: commissionAmount,
        description: `Comisión empresa (${companyInfo.companyCommission}%)`
      };

      // Actualizar el formulario con el nuevo pago
      form.setFieldsValue({
        payments: [...currentPayments, newPayment]
      });
      
      message.success(`Comisión de empresa agregada: $${commissionAmount.toLocaleString()} (se guardará al enviar el formulario)`);
    } catch (error) {
      console.error("Error al agregar comisión de empresa:", error);
      message.error("Error al agregar la comisión de empresa");
    }
  };

  return (
    <>
      <div className="flex justify-center flex-col items-center w-full">
        {!isModal && (
          <div className="flex justify-start w-full items-center mb-3">
            <Button
              onClick={() => {
                navigate(-1);
              }}
              icon={<ChevronLeft size={16} />}
              type="text"
              className="my-2 me-2"
            >
              Volver
            </Button>
            <span className="font-semibold text-lg">
              {passengerId ? "Edición de Pasajero" : "Creación de Pasajero"}
            </span>
          </div>
        )}

        <div className="">
          {isSelectedExcursionFinished && (
            <Alert
              message="Excursión finalizada"
              description={
                <div>
                  La excursión del passajero seleccionado ya está finalizada y no se puede modificar desde aquí. Para poder editar este pasajero, active la excursión en la administración.
                  <div style={{ marginTop: 8 }}>
                    <Button
                      type="link"
                      onClick={() => {
                        const excursionId = selectedExcursion?.value;
                        if (excursionId) {
                          window.open(
                            `${process.env.REACT_APP_API_URL}admin/schedule_excursion/scheduledexcursion/${excursionId}/change/`,
                            '_blank'
                          );
                        }
                      }}
                    >
                      Ir al detalle de la excursión
                    </Button>
                  </div>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            className="w-full"
            disabled={isSelectedExcursionFinished}
          >
            <Row gutter={16} className="bg-primary">
              <Col span={12}>
                <Form.Item
                  label="Grupo"
                  name="group"
                  className="mb-2"
                  rules={[
                    {
                      required: true,
                      message: "¡Por favor seleccione un grupo!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    labelInValue
                    options={groups.results?.map((group) => ({
                      label: `${group.selectorDisplay} (${dayjs(
                        group.checkInDate
                      ).format("ddd DD MMM")})`,
                      value: group.id,
                    }))}
                    optionFilterProp={"label"}
                    placeholder="Seleccione un grupo"
                    disabled={isSelectedGroupMissing}
                  />
                </Form.Item>
                <Form.Item
                  label="Hotel"
                  name="hotel"
                  className="mb-2"
                  rules={[
                    {
                      required: true,
                      message: "¡Por favor seleccione un Hotel!",
                    },
                  ]}
                >
                  <FetchSelect
                    placeholder="Seleccione un hotel"
                    searchParam={"name"}
                    elementFetcher={getHotels}
                    fieldNames={{ label: "name", value: "id" }}
                    labelShow={(hotel) => {
                      if (!hotel) return "";

                      return `${hotel.name} (${hotel.street} ${hotel.streetNumber})`;
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label="Vendedor"
                  name="seller"
                  className="mb-2"
                  rules={[
                    {
                      required: true,
                      message: "¡Por favor seleccione un Vendedor!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    labelInValue
                    options={users?.map((user) => ({
                      label: `${user.firstName} ${user.lastName} (${
                        roleFormat[user.userType]
                      })`,
                      value: user.id,
                    }))}
                    optionFilterProp={"label"}
                    placeholder="Seleccione un vendedor"
                    disabled={isSelectedExcursionFinished || !selectedGroup}
                    onChange={(value) => {
                      form.setFieldsValue({
                        seller: value,
                        payments: [
                          ...(form.getFieldValue("payments") || []),
                          {
                            user: value,
                            paymentMethod: null,
                            amount: 0,
                          },
                        ],
                      });
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label="Excursión"
                  name="scheduledExcursion"
                  className="mb-2"
                  rules={[
                    {
                      required: true,
                      message: "¡Por favor seleccione una excursion!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    labelInValue
                    options={excursions.results?.map((excursion) => ({
                      label: `${excursion.excursionType.name} (${dayjs(
                        excursion.day
                      ).format("ddd DD MMM")})`,
                      value: excursion.id,
                    }))}
                    optionFilterProp={"label"}
                    placeholder="Seleccione una excursión"
                  />
                </Form.Item>
                <Form.Item
                  label="Observaciones"
                  name="observation"
                  className="mb-2"
                >
                  <Input.TextArea placeholder="Ingrese observaciones" />
                </Form.Item>
                <Form.Item
                  label="Observaciones del Guía"
                  name="guideObservation"
                  className="mb-2"
                >
                  <Input.TextArea placeholder="Ingrese observaciones del guía" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Nombre"
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: "¡Por favor ingrese su nombre!",
                    },
                  ]}
                  className="mb-2"
                >
                  <Input placeholder="Nombre" />
                </Form.Item>
                <Form.Item label="Adulto" className="mb-2">
                  <Space.Compact className="w-full">
                    <Form.Item name="adultsCount" noStyle>
                      <InputNumber placeholder="Adulto" className="w-1/2" />
                    </Form.Item>
                    <Form.Item name="adultsAbsentCount" noStyle>
                      <InputNumber
                        placeholder="Ausencia de Adulto"
                        className="w-1/2"
                      />
                    </Form.Item>
                  </Space.Compact>
                </Form.Item>
                <Form.Item label="Menor" className="mb-2">
                  <Space.Compact className="w-full">
                    <Form.Item name="minorsCount" noStyle>
                      <InputNumber placeholder="Menor" className="w-1/2" />
                    </Form.Item>
                    <Form.Item name="minorsAbsentCount" noStyle>
                      <InputNumber
                        placeholder="Ausencia de Menor"
                        className="w-1/2"
                      />
                    </Form.Item>
                  </Space.Compact>
                </Form.Item>
                <Form.Item label="Bebé" className="mb-2">
                  <Space.Compact className="w-full">
                    <Form.Item name="babyCount" noStyle>
                      <InputNumber placeholder="Bebé" className="w-1/2" />
                    </Form.Item>
                    <Form.Item name="babyAbsentCount" noStyle>
                      <InputNumber
                        placeholder="Ausencia de Bebé"
                        className="w-1/2"
                      />
                    </Form.Item>
                  </Space.Compact>
                </Form.Item>
                <Form.Item label="Liberado" className="mb-2">
                  <Space.Compact className="w-full">
                    <Form.Item name="freeCount" noStyle>
                      <InputNumber placeholder="Liberado" className="w-1/2" />
                    </Form.Item>
                    <Form.Item name="freeAbsentCount" noStyle>
                      <InputNumber
                        placeholder="Ausencia de Liberado"
                        className="w-1/2"
                      />
                    </Form.Item>
                  </Space.Compact>
                </Form.Item>
              </Col>
            </Row>

            {/* Sección de Descuento - Siempre visible */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium mb-2">Descuento</div>

              <Form.Item name="discountType" initialValue="none" hidden>
                <input />
              </Form.Item>

              <div className="flex gap-2 flex-wrap mb-3">
                <Button
                  type={discountType === "none" || !discountType ? "primary" : "default"}
                  size="small"
                  onClick={() => form.setFieldValue("discountType", "none")}
                >
                  Sin descuento
                </Button>
                <Button
                  type={discountType === "percentage" ? "primary" : "default"}
                  size="small"
                  onClick={() => form.setFieldValue("discountType", "percentage")}
                >
                  % Descuento
                </Button>
                <Button
                  type={discountType === "fixed" ? "primary" : "default"}
                  size="small"
                  onClick={() => form.setFieldValue("discountType", "fixed")}
                >
                  Precio fijo
                </Button>
              </div>

              {discountType === "percentage" && (
                <div className="mb-2">
                  <Form.Item
                    name="discountPercentage"
                    className="mb-0"
                    rules={[
                      {
                        type: "number",
                        min: 0,
                        max: 100,
                        message: "El descuento debe ser entre 0 y 100%",
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      addonAfter="%"
                      placeholder="Ej: 20"
                      style={{ width: 120 }}
                    />
                  </Form.Item>
                </div>
              )}

              {discountType === "fixed" && (
                <Row gutter={16} className="mb-2">
                  <Col span={12}>
                    <Form.Item name="customPriceAdult" label="Precio Adulto" className="mb-0">
                      <InputNumber
                        min={0}
                        className="w-full"
                        formatter={(value) =>
                          `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }
                        parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                        placeholder="Precio adulto"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="customPriceMinor" label="Precio Menor" className="mb-0">
                      <InputNumber
                        min={0}
                        className="w-full"
                        formatter={(value) =>
                          `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }
                        parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                        placeholder="Precio menor"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {discountType !== "none" && discountType && (
                <Form.Item
                  name="discountAffectsCommission"
                  valuePropName="checked"
                  initialValue={true}
                  className="mb-0"
                >
                  <Checkbox>El descuento afecta las comisiones</Checkbox>
                </Form.Item>
              )}
            </div>

            <Row>
              <div className="flex justify-center w-full my-3">
                <span className="text-lg">Caja</span>
              </div>
            </Row>

            <Form.List name="payments">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row key={key} gutter={8} align="middle">
                      <Col span={10}>
                        <Form.Item
                          {...restField}
                          name={[name, "user"]}
                          rules={[
                            {
                              required: true,
                              message: "Seleccione un usuario",
                            },
                          ]}
                        >
                          <Select
                            popupMatchSelectWidth={false}
                            showSearch
                            labelInValue
                            options={users?.map((user) => ({
                              label: `${user.firstName} ${user.lastName} (${
                                roleFormat[user.userType]
                              })`,
                              value: user.id,
                            }))}
                            optionFilterProp={"label"}
                            placeholder="Seleccione un usuario"
                            disabled={!selectedGroup}
                            onChange={(value) =>
                              handleUserChange(value.value, name)
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, "paymentMethod"]}
                        >
                          <Select
                            placeholder="Seleccione un método de pago"
                            popupMatchSelectWidth={false}
                            disabled={
                              !userTypes[name] ||
                              !userTypes[name].includes("OFFICE")
                            }
                          >
                            {paymentMethods.map((method) => (
                              <Option key={method.id} value={method.id}>
                                {method.name} - {method.box}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, "amount"]}
                          rules={[
                            { required: true, message: "Ingrese un monto" },
                          ]}
                        >
                          <InputNumber
                            placeholder="Monto"
                            className="w-full"
                            formatter={(value) =>
                              `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                            }
                            parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <DeleteOutlined
                          className="text-red-600 mb-[30px]"
                          onClick={() => {
                            remove(name);
                            setUserTypes((prev) => {
                              const updated = { ...prev };
                              delete updated[name];
                              return updated;
                            });
                          }}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Agregar Pago
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Row>
              <Col span={5}>
                <Statistic
                  title="Pagado"
                  value={amountPayed}
                  formatter={formatter}
                  valueStyle={{ color: "#3f8600" }}
                />
              </Col>

              <Col span={5}>
                <Statistic
                  title="Adeuda"
                  value={debt}
                  formatter={formatter}
                  valueStyle={{ color: "#cf1322" }}
                />
              </Col>

              <Col span={6} offset={8}>
                <Statistic
                  title="Total a pagar"
                  value={totalToPay}
                  formatter={formatter}
                />
                {discountType !== "none" && totalWithoutDiscount > totalToPay && (
                  <div className="text-sm text-gray-500 mt-1">
                    Sin descuento: ${totalWithoutDiscount.toLocaleString()}{" "}
                    <span className="text-green-600">
                      (-${(totalWithoutDiscount - totalToPay).toLocaleString()})
                    </span>
                  </div>
                )}
              </Col>
            </Row>

            <Alert
              message="Tener en cuenta que si cambia las cantidades el monto no se actualizará automáticamente"
              type="warning"
              showIcon
              className="mb-3"
            />
            <Button
              type="default"
              icon={<DollarSign size={16} />}
              onClick={handleCoordinatorCommission}
              className="mb-2 me-2"
              loading={loading.post}
              disabled={
                isSelectedExcursionFinished || (
                  passengerId 
                    ? !passenger?.group?.company?.id // En edición: necesita empresa del pasajero
                    : !selectedGroup // En creación: solo necesita grupo seleccionado
                )
              }
            >
              Agregar monto comisión coordinador
              {currentGroupCommission?.coordinatorCommission && !passengerId && 
                ` (${currentGroupCommission.coordinatorCommission}%)`
              }
              {passenger?.group?.company && passengerId && 
                ` (${passenger.group.company.name})`
              }
            </Button>

            <Button
              type="default"
              icon={<Building2 size={16} />}
              onClick={handleCompanyCommission}
              className="mb-3"
              loading={loading.post}
              disabled={
                isSelectedExcursionFinished || (
                  passengerId 
                    ? !passenger?.group?.company?.id // En edición: necesita empresa del pasajero
                    : !selectedGroup // En creación: solo necesita grupo seleccionado
                )
              }
            >
              Añadir pago comisión empresa
              {currentGroupCommission?.companyCommission && !passengerId && 
                ` (${currentGroupCommission.companyCommission}%)`
              }
              {passenger?.group?.company && passengerId && 
                ` (${passenger.group.company.name})`
              }
            </Button>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="mt-3 me-2"
                loading={loading.post}
                disabled={isSelectedExcursionFinished}
              >
                {passengerId ? "Editar Pasajero" : "Crear Pasajero"}
              </Button>

              {passengerId && (
                <Popconfirm
                  title="¿Estás seguro de que deseas eliminar este pasajero?"
                  onConfirm={async () => {
                    await deletePassenger(passengerId);
                    navigate("/excursiones");
                  }}
                  okText="Sí"
                  cancelText="No"
                >
                  <Button
                    color="danger"
                    variant="solid"
                    loading={loading.delete}
                  >
                    Eliminar Pasajero
                  </Button>
                </Popconfirm>
              )}
            </Form.Item>
          </Form>
        </div>
      </div>

    </>
  );
}

export default PassengerForm;
